import { Component } from '@theme/component';

/**
 * Aufklapp-Liste: hoechstens eine Zeile ist offen. Klick, Tippen oder Tastatur oeffnet eine Zeile,
 * ein erneuter Klick auf die offene schliesst sie (dann sind alle zu). Bewusst nicht beim Ueberfahren.
 * Im Theme-Editor oeffnet sich die ausgewaehlte Zeile.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [rows]
 * @property {HTMLButtonElement[]} [triggers]
 *
 * @extends {Component<Refs>}
 */
class FeatureExpandComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #preloadObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Fotos laden und dekodieren, sobald die Liste in die Naehe kommt – nicht erst beim Klick,
    // sonst konkurriert das Dekodieren mit der Aufklapp-Animation um dieselben Frames.
    this.#preloadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.#preloadObserver?.disconnect();
        for (const image of this.querySelectorAll('.feature-expand__image')) {
          if (!(image instanceof HTMLImageElement)) continue;
          image.loading = 'eager';
          image.decode().catch(() => {});
        }
      },
      { rootMargin: '100% 0px' }
    );
    this.#preloadObserver.observe(this);

    if (Shopify.designMode) document.addEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#preloadObserver?.disconnect();
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  /** @param {Event} event */
  toggle(event) {
    const row = /** @type {HTMLElement} */ (event.target).closest('.feature-expand__row');
    if (!(row instanceof HTMLElement)) return;
    this.#open(row.hasAttribute('data-open') ? null : row);
  }

  /** @param {Event & { detail?: { blockId?: string } }} event */
  #handleBlockSelect = (event) => {
    const row = this.refs.rows?.find((entry) => entry.dataset.blockId === event.detail?.blockId);
    if (row) this.#open(row);
  };

  /** @param {HTMLElement | null} target – null schliesst alle */
  #open(target) {
    for (const row of this.refs.rows ?? []) {
      const open = row === target;
      row.toggleAttribute('data-open', open);
      row.querySelector('.feature-expand__trigger')?.setAttribute('aria-expanded', String(open));
    }
  }
}

if (!customElements.get('feature-expand-component')) {
  customElements.define('feature-expand-component', FeatureExpandComponent);
}
